import { ApiResponse } from "@affinite/common";
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, map, of, throwError } from "rxjs";
import { AddressBookRecord } from "../models/letters/addressBookRecord";
import { Letter } from "../models/letters/letter";
import { LetterParameters } from "../models/letters/letterParameters";
import { LetterResult } from "../models/letters/letterResult";
import { LetterTemplate } from "../models/letters/letterTemplate";
import { SmartCommProject } from "../models/letters/smartCommProject";
import { UserEntryParameters } from "../models/letters/userEntryParameters";
import { TempUserEntryParameters } from "../models/letters/userTempEntryParameters";
import { LogoImage } from "../models/reporting/logoImage";
import { ReportParams } from "../models/reporting/reportParams";
import { AppSettings } from "../shared/appsettings";
import { CacheService } from "./cache.service";
import { TokenStorageService } from "./token-storage.service";

@Injectable({
  providedIn: 'root'
})

export class ReportService {
  private reportServiceBaseUrl!: string;
  private reportSubj = new BehaviorSubject<string>("");
  reportObs = this.reportSubj.asObservable();
  private letterGUIDSubj = new BehaviorSubject<LetterResult>(new LetterResult());
  letterGUIDObs = this.letterGUIDSubj.asObservable();
  private reportFileSubj = new BehaviorSubject<ArrayBuffer>(new ArrayBuffer(0));
  reportFileObs = this.reportFileSubj.asObservable();
  private addressBookSubj = new BehaviorSubject<AddressBookRecord[]>([]);
  addressBookObj = this.addressBookSubj.asObservable();
  private userEntrySubj = new BehaviorSubject<boolean>(false);
  userEntryObs = this.userEntrySubj.asObservable();
  private letterTemplateSubj = new BehaviorSubject<LetterTemplate[]>([]);
  letterTemplateObs = this.letterTemplateSubj.asObservable();
  smartCommProjectListCache: SmartCommProject[] = [];

  constructor(private http: HttpClient,
    private appConfig: AppSettings,
    private tokenService: TokenStorageService,
    private cacheService: CacheService) {
    this.reportServiceBaseUrl = this.appConfig.reportServiceBaseUrl;
  }

  getSSRSReport(authIntakeId: number, reportPath: string | undefined, freeFormCount: number, authDetailNumber: string): Observable<ApiResponse<any>> {
    const parameters = new LetterParameters();
    parameters.authIntakeId = authIntakeId;
    parameters.reportName = reportPath!;
    parameters.freeFormCount = freeFormCount;
    parameters.authDetailNumber = authDetailNumber;
    parameters.timeZoneOffset = this.getTimeZoneOffset();
    let reportUrl = this.reportServiceBaseUrl + "Report/GetSSRSReport";
    return this.http.post<ApiResponse<any>>(reportUrl, JSON.stringify(parameters)).pipe(
      catchError((error: HttpErrorResponse) => { return throwError(() => error); })
    );
  }


  getReportFile(authIntakeNotificationId: number) {
    let options = { 'responseType': 'arraybuffer' as 'json' };

    let reportUrl = this.reportServiceBaseUrl + "Report/GetReportFile" + "?" + "authIntakeNotificationId=" + authIntakeNotificationId;
    this.http.get<ArrayBuffer>(reportUrl, options).subscribe(
      data => {
        this.reportFileSubj.next(data);
      }
    );
  }

  getAddressBook(memberId: string) {
    let headers = new HttpHeaders({ 'x-api-version': '1.0' });

    let reportUrl = this.reportServiceBaseUrl + "Report/GetAddressBook" + "?" + "memberId=" + memberId;
    this.http.get<ApiResponse<AddressBookRecord[]>>(reportUrl, { headers }).subscribe(
      data => {
        this.addressBookSubj.next(data.data!);
      }
    );
  }

  getLetterTemplates() {
    let reportUrl = this.reportServiceBaseUrl + "Report/GetLetterTemplates";
    this.http.get<ApiResponse<LetterTemplate[]>>(reportUrl).subscribe(
      data => {
        if (data.succeeded == true)
          this.letterTemplateSubj.next(data.data!);
      }
    );
  }

  generateReportToSend(authIntakeId: string, reportName: string | undefined, freeFormCount: number, letterTypeId: number | undefined, authDetailNumber: string,
    isCover: boolean, memberId: string, address: AddressBookRecord,
    authDetailId: number, tempFreeFormDataId: number = 0, smartCommProjectId: string | undefined, smartCommBatchConfigResId: string | undefined,
    smartCommTemplateId: string | undefined, templateSourceId: number | undefined, isSaveReport: boolean, queueFaxWorkflow: boolean = false): Observable<ApiResponse<LetterResult>> {
    const parameters = new LetterParameters();
    parameters.authIntakeId = Number(authIntakeId);
    parameters.tempFreeFormDataId = tempFreeFormDataId;
    parameters.reportName = reportName!;
    parameters.freeFormCount = freeFormCount;
    parameters.authDetailNumber = authDetailNumber;
    parameters.letterTypeId = letterTypeId!;
    parameters.userName = this.tokenService.getUser().profile.preferred_username;
    parameters.isCover = isCover;
    parameters.memberId = memberId;
    parameters.isUpdate = false;
    parameters.timeZoneOffset = this.getTimeZoneOffset();
    parameters.address = address;
    parameters.authDetailId = authDetailId;
    parameters.isSaveReport = isSaveReport;
    parameters.smartCommProjectId = smartCommProjectId;
    parameters.smartCommBatchConfigResId = smartCommBatchConfigResId;
    parameters.smartCommTemplateId = smartCommTemplateId;
    parameters.templateSourceId = templateSourceId;
    parameters.queueFaxWorkflow = queueFaxWorkflow;

    const userName = this.tokenService.getUser().profile.preferred_username;
    let reportUrl = this.reportServiceBaseUrl + "Report/GetReportToSend/";
    return this.http.post<ApiResponse<LetterResult>>(reportUrl, JSON.stringify(parameters)).pipe(
      catchError((error: HttpErrorResponse) => { return throwError(() => error); })
    )
  }

  saveLetter(letter: Letter) {
    let saveLetterUrl = this.reportServiceBaseUrl + "Report/SaveLetter/";
    return this.http.post<ApiResponse<LetterResult>>(saveLetterUrl, JSON.stringify(letter));
  }

  saveUserEntry(tempFreeFormDataId: number, authIntakeNotificationId: number) {
    const params: UserEntryParameters = { tempFreeFormDataId: tempFreeFormDataId, authIntakeNotificationId: authIntakeNotificationId };
    let saveUserEntryUrl = this.reportServiceBaseUrl + "Report/SaveUserEntry/";
    return this.http.post<ApiResponse<string>>(saveUserEntryUrl, JSON.stringify(params));
  }

  saveTempUserEntry(authDetailID: number, data: string[] | [] | undefined) {
    const userName = this.tokenService.getUser().profile.preferred_username;
    const params: TempUserEntryParameters = { authDetailId: authDetailID, userEntries: data, userName: userName };
    let url = this.reportServiceBaseUrl + "Report/SaveTempUserEntry/";
    return this.http.post<ApiResponse<number>>(url, JSON.stringify(params));
  }

  getTimeZoneOffset(): number {
    return - new Date().getTimezoneOffset() / 60;
  }

  getReport(reportName: string, reportId: string | null, format: string, reportFolder: string, authDetailID: number, addAttachments: boolean): Observable<HttpEvent<Blob>> {
    let headers = new HttpHeaders({ 'x-api-version': '1.0' });
    let options = { headers: headers };
    let parameters: ReportParams[] = [];
    parameters.push({ key: 'reportName', value: reportName, parameter: false });

    if (reportId) {
      parameters.push({ key: 'reportId', value: reportId, parameter: true });
    }
    parameters.push({ key: 'reportFolder', value: reportFolder, parameter: false });
    if (format) {
      parameters.push({ key: 'format', value: format, parameter: false });
    }
    parameters.push({ key: 'AuthDetailID', value: authDetailID.toString(), parameter: true });

    if (addAttachments) {
      parameters.push({ key: 'addAttachments', value: 'true', parameter: false });
    }

    let reportUrl = this.reportServiceBaseUrl + "Report/GetReport";

    return this.http.request<Blob>(new HttpRequest(
      'GET',
      `${reportUrl}?reportParameters=${JSON.stringify(parameters)}`,
      null,
      {
        reportProgress: true,
        responseType: 'blob',
        headers: headers
      }));
  }

  getLogoImages(authIntakeId: number, authDetailNumber: string, logoType: string = 'body', logoKey: string):
    Observable<LogoImage[]> | void {
    let headers = new HttpHeaders({ 'x-api-version': '1.0' });
    let params = {
      mvdId: authIntakeId,
      authIntakeId: authIntakeId,
      authDetailNumber: authDetailNumber,
      logoType: logoType,
      logoKey: logoKey
    }
    let reportUrl = this.reportServiceBaseUrl + "Report/GetLogoImages";

    return this.http.post<LogoImage[]>(reportUrl, params, { headers: headers }).pipe(
      // map((response: ApiResponse<LogoImage[]>) => {});
      map(response => { return response; })
    );

  }

  getSmartCommProjects(includeRights: boolean = true, forceRefresh: boolean = false): Observable<SmartCommProject[] | undefined> {
    if (!forceRefresh && this.smartCommProjectListCache.length > 0) {
      return of(this.smartCommProjectListCache);
    }

    let headers = new HttpHeaders({ 'x-api-version': '1.0' });
    let reportUrl = this.reportServiceBaseUrl + "Report/GetSmartCommProjectList";
    let params = {
      includeRights: includeRights
    }

    return this.http.get<SmartCommProject[]>(reportUrl, { headers: headers, params: params }).pipe(
      map((response: SmartCommProject[]) => {
        if (response) {
          this.smartCommProjectListCache = response;
          return response;
        }
        return undefined;
      })
    );
  }

  getSmartCommReleasedProjects(forceRefresh: boolean = false, workflowStepNo = 1000,): Observable<SmartCommProject[] | undefined> {
    let headers = new HttpHeaders({ 'x-api-version': '1.0' });
    let reportUrl = this.reportServiceBaseUrl + "Report/GetSmartCommReleasedProjectList";
    let params = {
      workflowStepNo: workflowStepNo
    }
    const cacheKey = reportUrl + workflowStepNo;
    const cacheResult = this.cacheService.get(cacheKey);
    if (cacheResult && !forceRefresh) {
      return of(cacheResult);
    }
    return this.http.get<SmartCommProject[]>(reportUrl, { headers: headers, params: params }).pipe(
      map((response: SmartCommProject[]) => {
        if (response) {
          this.cacheService.set(cacheKey, response);
          return response;
        }
        return undefined;
      })
    );
  }
}
